import { useState } from "react";
import emailjs from "@emailjs/browser";
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithPopup, GoogleAuthProvider, OAuthProvider, updateProfile } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, setDoc, getDocs, collection, query, where } from "firebase/firestore";

const EMAILJS_SERVICE_ID = "service_6s4y6ci";
const EMAILJS_TEMPLATE_ID_WELCOME = "template_w544ezp";
const EMAILJS_TEMPLATE_ID_OWNER = "template_a2ue8ic";
const EMAILJS_PUBLIC_KEY = "IqticZ4G35CQ30ugZ";

function generateMusicID(type = "MUL") {
  const year = new Date().getFullYear();
  const num = Math.floor(100000 + Math.random() * 900000);
  return `${type}-${year}-${num}`;
}

export default function Signup({ onLogin, onSignup }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const strengthLevel = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 10 ? 2 : password.length < 14 ? 3 : 4;
  const strengthColors = ["#f0f0f0", "#dc2626", "#f59e0b", "#3b82f6", "#16a34a"];
  const strengthLabels = ["", "Too short", "Weak", "Good", "Strong"];

  async function checkUsername(value) {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_.]/g, "");
    setUsername(cleaned);
    if (cleaned.length < 3) { setUsernameAvailable(null); return; }
    setCheckingUsername(true);
    try {
      const q = query(collection(db, "users"), where("username", "==", cleaned));
      const snap = await getDocs(q);
      setUsernameAvailable(snap.empty);
    } catch { setUsernameAvailable(null); }
    setCheckingUsername(false);
  }

  async function createUserProfile(user, usernameValue) {
    const musicId = generateMusicID("MUL");
    const profile = {
      uid: user.uid, email: user.email,
      displayName: user.displayName || usernameValue || user.email?.split("@")[0],
      username: usernameValue || user.email?.split("@")[0].toLowerCase().replace(/[^a-z0-9_.]/g, ""),
      musicId, memberType: "Music Lover", memberStatus: "none",
      memberSince: new Date().toISOString(), photoURL: user.photoURL || "",
      followers: 0, following: 0, bio: "",
    };
    await setDoc(doc(db, "users", user.uid), profile);
    return musicId;
  }

  async function sendWelcomeEmail(toName, toEmail, musicId) {
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID_WELCOME, {
        to_name: toName || "Music Lover", to_email: toEmail,
        from_name: "MyMusic Universal Music Society",
        message: `Welcome to MyMusic! Your Music ID: ${musicId}. Start exploring millions of songs and connect with artists worldwide.`,
        app_link: "https://mymusicstream.online",
      }, EMAILJS_PUBLIC_KEY);
    } catch {}
  }

  async function sendOwnerNotification(toName, toEmail, musicId) {
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID_OWNER, {
        user_name: toName || "Unknown", user_email: toEmail,
        signup_time: new Date().toLocaleString(),
        subject: `New User — ${toName}`,
        message: `New member joined MyMusic.\nMusic ID: ${musicId}\nJoined: ${new Date().toLocaleString()}`,
      }, EMAILJS_PUBLIC_KEY);
    } catch {}
  }

  async function handleGoogle() {
    setError(""); setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const isNewUser = result._tokenResponse?.isNewUser;
      if (isNewUser) {
        const musicId = await createUserProfile(user, "");
        await sendWelcomeEmail(user.displayName || "Music Lover", user.email, musicId);
        await sendOwnerNotification(user.displayName || "Music Lover", user.email, musicId);
      }
      onLogin();
    } catch { setError("Google sign-in failed. Try again."); }
    setLoading(false);
  }

  async function handleApple() {
    setError(""); setLoading(true);
    try {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email"); provider.addScope("name");
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const musicId = await createUserProfile(user, "");
      await sendWelcomeEmail(user.displayName || "Music Lover", user.email, musicId);
      await sendOwnerNotification(user.displayName || "Music Lover", user.email, musicId);
      onSignup();
    } catch { setError("Apple sign-in failed. Please try again."); }
    setLoading(false);
  }

  async function handleSignup() {
    setError(""); setSuccess("");
    if (!email || !password || !confirmPassword) { setError("Please fill in all fields."); return; }
    if (!username || username.length < 3) { setError("Choose a username (min 3 characters)."); return; }
    if (usernameAvailable === false) { setError("Username already taken. Choose another."); return; }
    if (password !== confirmPassword) { setError("Passwords don't match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: username });
      await sendEmailVerification(userCredential.user);
      const musicId = await createUserProfile(userCredential.user, username);
      await sendWelcomeEmail(username, email, musicId);
      await sendOwnerNotification(username, email, musicId);
      setSuccess(`Account created! Your Music ID: ${musicId}`);
      setTimeout(() => onLogin(), 3500);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") setError("This email is already registered. Try signing in.");
      else if (err.code === "auth/invalid-email") setError("Invalid email address.");
      else setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  }

  return (
    <div style={S.page}>
      <style>{CSS}</style>

      {/* LEFT — FORM */}
      <div style={S.left}>
        <div style={S.card}>
          <Logo />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid #e5e5e5", borderRadius: 100, padding: "6px 14px", marginBottom: 24 }}>
            <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: "none", stroke: "#000", strokeWidth: 2 }}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#000", textTransform: "uppercase" }}>Universal Music Society</span>
          </div>

          <h1 style={S.heading}>Join the<br />Society.</h1>
          <p style={S.sub}>Get your Music ID and connect with artists worldwide.</p>

          {error && <div style={S.errorBox}>{error}</div>}
          {success && <div style={S.successBox}>{success}</div>}

          <button onClick={handleGoogle} disabled={loading} style={S.socialBtn} className="social-btn">
            <GoogleSVG /><span>Continue with Google</span>
          </button>
          <button onClick={handleApple} disabled={loading} style={{ ...S.socialBtn, marginBottom: 28 }} className="social-btn">
            <AppleSVG /><span>Continue with Apple</span>
          </button>

          <Divider text="or sign up with email" />

          {/* Username */}
          <div style={{ marginBottom: 16 }}>
            <label style={S.fieldLabel}>Username</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...S.input, borderColor: username.length >= 3 ? usernameAvailable === true ? "#16a34a" : usernameAvailable === false ? "#dc2626" : "#e5e5e5" : "#e5e5e5" }}
                type="text" placeholder="e.g. musiclover_mm" value={username}
                onChange={e => checkUsername(e.target.value)} className="auth-input" />
              <div style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 13 }}>
                {checkingUsername ? "..." : username.length >= 3 ? usernameAvailable === true ? "✓" : usernameAvailable === false ? "✗" : "" : ""}
              </div>
            </div>
            {username.length >= 3 && (
              <p style={{ fontSize: 11, marginTop: 6, color: usernameAvailable === true ? "#16a34a" : usernameAvailable === false ? "#dc2626" : "#999" }}>
                {usernameAvailable === true ? `@${username} is available` : usernameAvailable === false ? "Username already taken" : "Checking..."}
              </p>
            )}
            {username.length > 0 && <p style={{ fontSize: 11, color: "#ccc", marginTop: 4 }}>mymusicstream.online/@{username}</p>}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={S.fieldLabel}>Email</label>
            <input style={S.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className="auth-input" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={S.fieldLabel}>Password</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...S.input, paddingRight: 44 }} type={showPass ? "text" : "password"} placeholder="Min. 6 characters" value={password} onChange={e => setPassword(e.target.value)} className="auth-input" />
              <button onClick={() => setShowPass(!showPass)} style={S.eyeBtn}>
                <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "none", stroke: "#bbb", strokeWidth: 1.8 }}>
                  {showPass ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                </svg>
              </button>
            </div>
            {password.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{ flex: 1, height: 2, borderRadius: 1, background: i <= strengthLevel ? strengthColors[strengthLevel] : "#f0f0f0", transition: "background 0.3s" }} />
                  ))}
                </div>
                <p style={{ fontSize: 11, color: strengthColors[strengthLevel] }}>{strengthLabels[strengthLevel]}</p>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={S.fieldLabel}>Confirm password</label>
            <input style={{ ...S.input, borderColor: confirmPassword.length > 0 ? password === confirmPassword ? "#16a34a" : "#dc2626" : "#e5e5e5" }}
              type="password" placeholder="Repeat your password" value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSignup()} className="auth-input" />
          </div>

          <p style={{ fontSize: 11, color: "#ccc", textAlign: "center", marginBottom: 20, lineHeight: 1.7 }}>
            By joining you agree to our{" "}
            <span style={{ color: "#000", textDecoration: "underline", cursor: "pointer" }}>Terms</span>,{" "}
            <span style={{ color: "#000", textDecoration: "underline", cursor: "pointer" }}>Privacy Policy</span> and{" "}
            <span style={{ color: "#000", textDecoration: "underline", cursor: "pointer" }}>Community Guidelines</span>
          </p>

          <button onClick={handleSignup} disabled={loading} style={S.btnDark} className="btn-dark-hover">
            {loading ? "Creating account..." : "Create account"}
          </button>

          <p style={S.footer}>Already a member? <span onClick={onLogin} style={S.link}>Sign in</span></p>
        </div>
      </div>

      {/* RIGHT — ID CARD PREVIEW */}
      <div style={S.right} className="auth-right-panel">
        <div style={{ width: "100%", maxWidth: 300 }}>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", color: "#bbb", marginBottom: 32, textTransform: "uppercase" }}>Your Music ID preview</p>

          {/* Live ID card preview */}
          <div style={{ background: "#000", borderRadius: 10, padding: 28, color: "#fff", marginBottom: 32 }}>
            <div style={{ fontSize: 9, color: "#555", letterSpacing: "0.16em", fontWeight: 600, marginBottom: 20, textTransform: "uppercase" }}>Universal Music Society — Official Member</div>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#111", border: "1px solid #222", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "none", stroke: "#fff", strokeWidth: 1.5 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </div>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, marginBottom: 4, minHeight: 28, color: username ? "#fff" : "#333" }}>
              {username ? `@${username}` : "@yourusername"}
            </div>
            <div style={{ fontSize: 11, color: "#444", fontFamily: "monospace", marginBottom: 20, letterSpacing: "0.06em" }}>
              MUL-{new Date().getFullYear()}-{username ? username.split("").reduce((a, c) => a + c.charCodeAt(0), 100000).toString().slice(0, 6) : "000000"}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 16, borderTop: "1px solid #111" }}>
              <div>
                <div style={{ fontSize: 9, color: "#444", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.1em" }}>Member type</div>
                <div style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>Music Lover</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, color: "#444", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.1em" }}>Since</div>
                <div style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>{new Date().getFullYear()}</div>
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 28 }}>
            {[
              "Stream millions of songs instantly",
              "Upload your original music",
              "Join global music communities",
              "Get your official Music ID",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", border: "1px solid #e5e5e5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  <svg viewBox="0 0 24 24" style={{ width: 9, height: 9, fill: "none", stroke: "#000", strokeWidth: 2.5 }}><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <span style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 40 }}>
      <img src="/logo192.png" alt="MyMusic" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600, letterSpacing: "-0.02em", color: "#000" }}>MyMusic</span>
    </div>
  );
}

function Divider({ text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
      <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
      <span style={{ fontSize: 11, color: "#ccc", fontWeight: 500, letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{text || "OR"}</span>
      <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
    </div>
  );
}

function GoogleSVG() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.1 0 5.9 1.1 8.1 2.9l6-6C34.5 3.5 29.5 1.5 24 1.5 14.8 1.5 7 7.3 3.7 15.3l7 5.4C12.4 14.1 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.1z"/>
      <path fill="#FBBC05" d="M10.7 28.8A14.4 14.4 0 0 1 9.5 24c0-1.7.3-3.3.8-4.8l-7-5.4A22.5 22.5 0 0 0 1.5 24c0 3.6.9 7 2.4 10l6.8-5.2z"/>
      <path fill="#34A853" d="M24 46.5c5.5 0 10.1-1.8 13.4-4.9l-7.6-5.9c-1.8 1.2-4.1 1.9-5.8 1.9-6.3 0-11.6-4.6-13.3-10.8l-6.8 5.2C7 39.2 14.8 46.5 24 46.5z"/>
    </svg>
  );
}

function AppleSVG() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#000">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: #fff; }
  .auth-input:focus { border-color: #000 !important; outline: none; }
  .auth-input::placeholder { color: #ccc !important; }
  .social-btn:hover { background: #f8f8f8 !important; }
  .btn-dark-hover:hover:not(:disabled) { opacity: 0.75; }
  .auth-right-panel { display: flex !important; }
  @media (max-width: 768px) { .auth-right-panel { display: none !important; } }
`;

const S = {
  page: { minHeight: "100vh", background: "#fff", display: "flex", fontFamily: "'DM Sans', sans-serif" },
  left: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "60px 40px", overflowY: "auto" },
  card: { width: "100%", maxWidth: 400 },
  right: { width: 440, background: "#fff", borderLeft: "1px solid #f0f0f0", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "60px 48px" },
  heading: { fontFamily: "'Instrument Serif', serif", fontSize: 44, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#000", marginBottom: 10 },
  sub: { fontSize: 14, color: "#999", lineHeight: 1.7, marginBottom: 32, fontWeight: 400 },
  errorBox: { background: "#fff5f5", border: "1px solid #fecaca", color: "#dc2626", padding: "12px 16px", borderRadius: 4, fontSize: 13, marginBottom: 20 },
  successBox: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a", padding: "12px 16px", borderRadius: 4, fontSize: 13, marginBottom: 20 },
  socialBtn: { width: "100%", padding: "13px 16px", borderRadius: 3, border: "1px solid #e5e5e5", background: "#fff", color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 10, transition: "background 0.15s" },
  fieldLabel: { display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#999", marginBottom: 8 },
  input: { width: "100%", padding: "13px 14px", borderRadius: 3, border: "1px solid #e5e5e5", background: "#fff", color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: "none", transition: "border-color 0.15s" },
  eyeBtn: { position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" },
  btnDark: { width: "100%", padding: "14px", borderRadius: 3, border: "none", background: "#000", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", letterSpacing: "0.02em", marginBottom: 24, transition: "opacity 0.15s" },
  link: { color: "#000", fontWeight: 600, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 },
  footer: { textAlign: "center", fontSize: 13, color: "#999" },
};